import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Eye, EyeOff, Pencil, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ConfirmDeleteDialog } from '@/components/shared'
import { ListPagination } from '@/components/shared/ListPagination'
import { TruncatedTextTooltip } from '@/components/shared/TruncatedTextTooltip'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { toast } from '@/components/ui/toast'
import { useListParams } from '@/hooks/useListParams'
import { useOptionalFees, useOptionalFeeMutations } from '@/hooks/useSettings'
import { getApiErrorMessage } from '@/services/api'
import { formatCurrency, formatDate } from '@/lib/format'
import { defaultFeeImageUrl, optionalFeeImageUrl } from '@/lib/optionalFeeIcons'
import { cn } from '@/lib/utils'
import plusIcon from '@/assets/icons/optional-fees/panel/plus.svg'
import type { OptionalFeeTemplate } from '@/types/billing'
import type { PaginatedMetadata } from '@/types'
import {
  OptionalFeeDialog,
  type OptionalFeeDialogValue,
} from './OptionalFeeDialog'

const card =
  'overflow-hidden rounded-2xl border border-[#e7e9ef] bg-white shadow-[0_8px_16px_-4px_rgba(38,45,66,0.1),0_1px_4px_-4px_rgba(38,45,66,0.05)]'
const headerCell =
  'h-10 border-b border-[#e7e9ef] bg-[#fbfbfc] px-3 py-2 text-left text-[10px] font-normal uppercase leading-[14px] text-[#3d434e]'
const bodyCell =
  'h-16 border-b border-[#e7e9ef] px-3 py-2 align-middle text-sm leading-[1.4] text-[#171a20]'
const actionButton =
  'inline-flex size-8 shrink-0 items-center justify-center rounded-lg border border-transparent bg-[#f4f5f8] p-1.5 text-[#272c36] transition-colors group-hover:bg-[#fbfbfc] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#ff5a1f] disabled:cursor-not-allowed disabled:opacity-50'
const accentActionButton =
  'hover:border-[#ff5a1f] hover:bg-[#fff1ea] hover:text-[#ff5a1f] group-hover:hover:bg-[#fff1ea]'
const destructiveActionButton =
  'hover:border-[#d63b33] hover:bg-[#fde9e8] hover:text-[#d63b33] group-hover:hover:bg-[#fde9e8]'

// function ActionIcon({ src }: { src: string }) {
//   const url = `url("${src}")`

//   return (
//     <span
//       aria-hidden='true'
//       className='block size-4 bg-current'
//       style={{
//         maskImage: url,
//         maskPosition: 'center',
//         maskRepeat: 'no-repeat',
//         maskSize: 'contain',
//         WebkitMaskImage: url,
//         WebkitMaskPosition: 'center',
//         WebkitMaskRepeat: 'no-repeat',
//         WebkitMaskSize: 'contain',
//       }}
//     />
//   )
// }

export function OptionalFeesPanel() {
  const { t } = useTranslation('settings')
  const { page, setPage, limit, setLimit } = useListParams()
  const { data, isLoading, isError } = useOptionalFees()
  const { create, update, remove } = useOptionalFeeMutations()
  const [editing, setEditing] = useState<OptionalFeeTemplate | null>(null)
  const [open, setOpen] = useState(false)
  const [toDelete, setToDelete] = useState<OptionalFeeTemplate | null>(null)
  const [dialogSession, setDialogSession] = useState(0)
  const fees = data?.data ?? []
  const total = fees.length
  const lastPage = Math.max(1, Math.ceil(total / limit))
  const currentPage = Math.min(page, lastPage)
  const start = (currentPage - 1) * limit
  const visibleFees = fees.slice(start, start + limit)
  const metadata: PaginatedMetadata | undefined = data
    ? {
        current_page: currentPage,
        per_page: limit,
        total,
        last_page: lastPage,
        from: total > 0 ? start + 1 : null,
        to: total > 0 ? Math.min(start + limit, total) : null,
      }
    : undefined

  const begin = (fee: OptionalFeeTemplate | null) => {
    setEditing(fee)
    setDialogSession(current => current + 1)
    setOpen(true)
  }

  const save = async (value: OptionalFeeDialogValue) => {
    if (editing) {
      await update.mutateAsync({
        id: editing.id,
        body: {
          name: value.name,
          amount: value.amount,
          is_active: value.is_active,
          ...(value.image_removed ||
          value.image_file_id !== editing.image_file_id
            ? { image_file_id: value.image_file_id }
            : {}),
        },
      })
    } else {
      await create.mutateAsync({
        name: value.name,
        amount: value.amount,
        is_active: value.is_active,
        image_file_id: value.image_file_id,
      })
    }
    toast({
      variant: 'success',
      title: t(editing ? 'optionalFees.updated' : 'optionalFees.created'),
    })
  }

  const toggle = async (fee: OptionalFeeTemplate) => {
    try {
      await update.mutateAsync({
        id: fee.id,
        body: { is_active: !fee.is_active },
      })
      toast({
        variant: 'success',
        title: t(
          fee.is_active ? 'optionalFees.deactivated' : 'optionalFees.activated'
        ),
      })
    } catch (error) {
      toast({ variant: 'destructive', title: getApiErrorMessage(error) })
    }
  }

  const destroy = async () => {
    if (!toDelete) return
    try {
      await remove.mutateAsync(toDelete.id)
      setToDelete(null)
      toast({ variant: 'success', title: t('optionalFees.deleted') })
    } catch (error) {
      toast({ variant: 'destructive', title: getApiErrorMessage(error) })
    }
  }

  return (
    <>
      <section className={`${card} flex flex-col`}>
        <header className='flex items-center gap-2 overflow-hidden pb-2 pr-6 pt-6'>
          <div className='min-w-0 flex-1 border-l-4 border-[#ff5a1f] px-6 py-1'>
            <h2 className='font-heading text-sm font-semibold leading-[1.2] text-[#171a20]'>
              {t('optionalFees.title')}
            </h2>
          </div>
          <Button
            className='h-10 shrink-0 gap-2 rounded-xl border border-[#ff5a1f] bg-[#ff5a1f] px-3 text-sm font-medium text-white shadow-none hover:bg-[#e6511c]'
            onClick={() => begin(null)}
          >
            <img src={plusIcon} alt='' className='size-4' />
            {t('optionalFees.add')}
          </Button>
        </header>
        <div className='flex flex-1 flex-col gap-4 px-6 pb-4 pt-4'>
          <Table className='min-w-[1124px] table-fixed border-collapse text-left'>
            <colgroup>
              <col />
              <col />
              <col />
              <col />
              <col className='w-[136px]' />
            </colgroup>
            <TableHeader>
              <TableRow className='border-0'>
                <TableHead className={headerCell}>
                  {t('optionalFees.name')}
                </TableHead>
                <TableHead className={cn(headerCell, 'text-center')}>
                  {t('common.status')}
                </TableHead>
                <TableHead className={cn(headerCell, 'text-center')}>
                  {t('optionalFees.amount')}
                </TableHead>
                <TableHead className={headerCell}>
                  {t('optionalFees.updatedAt')}
                </TableHead>
                <TableHead
                  className={`${headerCell} w-[136px] max-w-[136px] text-right`}
                >
                  {t('common.actions')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={5} className={`${bodyCell} text-center`}>
                    {t('optionalFees.loading')}
                  </TableCell>
                </TableRow>
              )}
              {isError && (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className={`${bodyCell} text-center text-destructive`}
                  >
                    {t('optionalFees.loadError')}
                  </TableCell>
                </TableRow>
              )}
              {!isLoading && !isError && fees.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className={`${bodyCell} text-center text-[#6c7285]`}
                  >
                    {t('optionalFees.empty')}
                  </TableCell>
                </TableRow>
              )}
              {!isLoading &&
                !isError &&
                visibleFees.map(fee => (
                  <TableRow key={fee.id} className='group border-0'>
                    <TableCell className={bodyCell}>
                      <div className='flex min-w-0 items-center gap-2.5'>
                        <span className='flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-[#fff1ea]'>
                          <img
                            src={
                              optionalFeeImageUrl(fee) ??
                              defaultFeeImageUrl(fee.type)
                            }
                            alt=''
                            className={
                              fee.image_file_id
                                ? 'size-full object-cover'
                                : 'size-4 object-contain'
                            }
                          />
                        </span>
                        <span className='min-w-0 truncate font-medium'>
                          {fee.name}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className={cn(bodyCell, 'text-center')}>
                      <span
                        className={`inline-flex rounded-lg px-1.5 py-1 text-xs font-normal leading-4 ${fee.is_active ? 'bg-[#d1fae5] text-[#065f46]' : 'bg-[#e6e6e6] text-[#1e1e1e]'}`}
                      >
                        {t(fee.is_active ? 'common.active' : 'common.inactive')}
                      </span>
                    </TableCell>
                    <TableCell className={cn(bodyCell, 'text-center')}>
                      {formatCurrency(fee.amount, 'EUR', 2)}
                    </TableCell>
                    <TableCell className={bodyCell}>
                      {formatDate(fee.updated_at)}
                    </TableCell>
                    <TableCell
                      className={`${bodyCell} w-[136px] max-w-[136px]`}
                    >
                      <div className='flex items-center gap-2'>
                        <TruncatedTextTooltip
                          content={t(
                            fee.is_active
                              ? 'optionalFees.hide'
                              : 'optionalFees.show'
                          )}
                          delayDuration={0}
                          alwaysShow
                          closeOnPointerDown
                          className='inline-flex shrink-0'
                        >
                          <button
                            type='button'
                            className={cn(actionButton, accentActionButton)}
                            aria-label={t(
                              fee.is_active
                                ? 'optionalFees.hide'
                                : 'optionalFees.show'
                            )}
                            disabled={update.isPending}
                            onClick={() => void toggle(fee)}
                          >
                            {fee.is_active ? (
                              <EyeOff className='size-4' />
                            ) : (
                              <Eye className='size-4' />
                            )}
                          </button>
                        </TruncatedTextTooltip>
                        <TruncatedTextTooltip
                          content={t('common:actions.edit')}
                          delayDuration={0}
                          alwaysShow
                          closeOnPointerDown
                          className='inline-flex shrink-0'
                        >
                          <button
                            type='button'
                            className={cn(actionButton, accentActionButton)}
                            aria-label={t('common:actions.edit')}
                            onClick={() => begin(fee)}
                          >
                            <Pencil className='size-4' />
                          </button>
                        </TruncatedTextTooltip>
                        <TruncatedTextTooltip
                          content={t('common:actions.delete')}
                          delayDuration={0}
                          alwaysShow
                          closeOnPointerDown
                          className='inline-flex shrink-0'
                        >
                          <button
                            type='button'
                            className={cn(
                              actionButton,
                              destructiveActionButton
                            )}
                            aria-label={t('common:actions.delete')}
                            onClick={() => setToDelete(fee)}
                          >
                            <Trash2 className='size-4' />
                          </button>
                        </TruncatedTextTooltip>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>

          <ListPagination
            metadata={metadata}
            page={currentPage}
            pageSize={limit}
            onPageChange={setPage}
            onPageSizeChange={size => {
              setLimit(size)
              setPage(1)
            }}
            showSummary={false}
          />
        </div>
      </section>

      <OptionalFeeDialog
        key={dialogSession}
        open={open}
        onOpenChange={setOpen}
        initial={
          editing
            ? {
                name: editing.name,
                amount: editing.amount,
                is_active: editing.is_active,
                image_file_id: editing.image_file_id,
                image_url: editing.image_url,
                icon_key: editing.icon_key,
                type: editing.type,
              }
            : null
        }
        onSave={save}
      />

      <ConfirmDeleteDialog
        open={!!toDelete}
        onOpenChange={value => {
          if (!value && !remove.isPending) setToDelete(null)
        }}
        title={t('optionalFees.deleteTitle')}
        description={t('optionalFees.deleteConfirm', { name: toDelete?.name })}
        confirmLabel={t('optionalFees.delete')}
        cancelLabel={t('optionalFees.cancel')}
        onConfirm={destroy}
        busy={remove.isPending}
      />
    </>
  )
}
